import { useState, useMemo, useEffect } from "react";
import {
  Building2,
  Users,
  ArrowDownCircle,
  ArrowUpCircle,
  Layers,
  AlertTriangle,
} from "lucide-react";
import { useInventory } from "../Context/InventoryContext";
import ProtectedRoute from "../Components/ProtectedRoute";

import AnimatedChart from "../Components/AnimatedChart";
import DashboardLayout from "../Layouts/DashboardLayout";
import KitchenOrderCard from "../Components/KitchenOrderCard";

export default function DashboardHome() {
  const [chartType, setChartType] = useState("revenue");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const inventoryCtx = useInventory();
  const lowStockAlerts = inventoryCtx?.lowStockAlerts || [];

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await fetch("/api/dashboard/", {
        credentials: "include",
      });

      const data = await res.json();
      console.log(data);

      if (data.status === "success") {
        setDashboard(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const dynamicAlerts = useMemo(() => {
    const alerts = [];

    lowStockAlerts.forEach((a) => {
      alerts.push({
        type: a.stock <= a.min * 0.5 ? "danger" : "warning",
        text: `${a.item} low at ${a.locationName} (${a.stock}/${a.min})`,
      });
    });

    const gstToday = 21700;
    if (gstToday > 20000) {
      alerts.push({
        type: "danger",
        text: "GST liability crossed safe threshold",
      });
    }

    const productionExcess = true;
    if (productionExcess) {
      alerts.push({
        type: "warning",
        text: "Production exceeding sales for last 3 days",
      });
    }

    return alerts;
  }, [lowStockAlerts]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <ProtectedRoute>
      <DashboardLayout>
        <div className="space-y-10">
          <section className="flex bg-white/5 p-4 rounded-2xl border border-white/10 flex-wrap gap-3">
            <ActionButton icon={Building2} label="Add Branch" />
            <ActionButton icon={Users} label="Add Staff" />
            <ActionButton icon={ArrowDownCircle} label="Add Inward" />
            <ActionButton icon={ArrowUpCircle} label="Add Outward" />
            <ActionButton icon={Layers} label="Add Category" />
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {dashboard.stats.map((s) => (
              <KpiCard
                key={s.label}
                label={s.label}
                value={
                  s.label.includes("Revenue")
                    ? `₹${Number(s.value).toLocaleString()}`
                    : s.value
                }
              />
            ))}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FinanceCard
              title="GST Collected (MTD)"
              value={`₹${dashboard.finance.gst.toLocaleString()}`}
            />

            <FinanceCard
              title="Payments Received"
              value={`₹${dashboard.finance.payments.toLocaleString()}`}
            />

            <FinanceCard
              title="Pending Receivables"
              value={`₹${dashboard.finance.pending.toLocaleString()}`}
              danger
            />
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <ChartBox title="Revenue & Orders">
              <ChartHeader
                options={["revenue", "orders", "gst"]}
                active={chartType}
                setActive={setChartType}
              />
              <AnimatedChart type={chartType} charts={dashboard?.charts} />
            </ChartBox>

            <ChartBox title="Top Selling Items">
              <AnimatedChart type="top-items" charts={dashboard?.charts} />
            </ChartBox>

            <ChartBox title="Production vs Sales">
              <AnimatedChart
                type="production-vs-sales"
                charts={dashboard?.charts}
              />
            </ChartBox>
          </section>

          <section className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-4">Intelligent Alerts</h3>

            {dynamicAlerts.length === 0 ? (
              <div className="text-emerald-400 text-sm">
                All systems operating normally
              </div>
            ) : (
              <div className="space-y-3">
                {dynamicAlerts.map((a, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm
            ${
              a.type === "danger"
                ? "bg-red-500/10 text-red-400"
                : "bg-amber-400/10 text-amber-400"
            }`}
                  >
                    <AlertTriangle size={16} />
                    {a.text}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
              <h3 className="font-serif text-lg mb-4">Recent Activity</h3>
              <ul className="space-y-3 text-sm text-gray-300">
                {dashboard.activities.map((a, i) => (
                  <li key={i}>• {a.text}</li>
                ))}
              </ul>
            </div>

            <div className="xl:col-span-2">
              <h3 className="font-serif text-lg mb-4">Live Kitchen Orders</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {dashboard.kitchen_orders.map((o) => (
                  <KitchenOrderCard key={o.id} order={o} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function ChartBox({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-8">
      <h3 className="font-serif text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ChartHeader({ options, active, setActive }) {
  return (
    <div className="flex gap-2 mb-4">
      {options.map((t) => (
        <button
          key={t}
          onClick={() => setActive(t)}
          className={`px-4 py-1.5 text-xs rounded-lg transition
            ${
              active === t
                ? "bg-amber-400 text-black font-semibold"
                : "bg-black/40 text-gray-400 hover:text-white"
            }`}
        >
          {t.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="relative rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-amber-400/40 transition">
      <p className="text-xs text-gray-400 tracking-widest uppercase">{label}</p>
      <h2 className="mt-3 text-3xl font-serif">{value}</h2>
    </div>
  );
}

function ActionButton({ icon: Icon, label }) {
  return (
    <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black/40 border border-white/10 text-sm hover:bg-white/10 transition">
      <Icon size={16} />
      {label}
    </button>
  );
}

function FinanceCard({ title, value, danger }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        danger
          ? "bg-red-500/10 border-red-500/30"
          : "bg-white/5 border-white/10"
      }`}
    >
      <p className="text-xs text-gray-400 tracking-widest uppercase">{title}</p>
      <h3 className={`mt-3 text-2xl font-serif ${danger && "text-red-400"}`}>
        {value}
      </h3>
    </div>
  );
}
